import { apiPost, apiDelete } from '../lib/api-utils';

// Company Types
export interface CompanyRole {
  id: number;
  role: string;
  company_id: number;
  created_at: string;
  updated_at: string;
}

export interface Employee {
  id: number;
  employee_name: string;
  employee_mobile: string;
  user_id: number;
  company_role_id: number;
  designation: string;
  employee_image: string;
  /** The employee's home branch. */
  company_branch_id?: number;
  /** `1` on flexible timing. The PROFILE flag on `company_employee` — not the
   *  per-day copy that rides the attendance row. */
  is_flexible_time?: number | string;
  /** The employee's OWN shift, company-local display strings (e.g.
   *  "10:15 AM"). Native reads these — not the branch's — whenever the
   *  employee is on fixed timing
   *  (`CheckInCheckOutViewController+UIConfiguration.swift:1021,1036`).
   *
   *  `get-company` serialises the whole `company_employee` row
   *  (`CompanyRepository.php:2399`, no `select()`, no `$hidden` on the model),
   *  so these have always been on the wire — they were simply never declared. */
  start_time?: string;
  end_time?: string;
  /** The same shift as UTC times ("04:45:00"). This is the pair native reads
   *  when it computes the length of a fixed employee's working day
   *  (`AttendanceDayWindow.swift:102-105`), and it avoids the AM/PM and
   *  locale questions the display strings carry. */
  start_time_utc?: string;
  end_time_utc?: string;
  /** `1` when this employee may work overtime at all. Gates the quota row's
   *  "+OT Nh" suffix, exactly as native gates it
   *  (`CheckInCheckOutViewController+UIConfiguration.swift:1799`). */
  is_overtime_allowed?: number | string;
}

export interface DetailedCompany {
  id: number;
  name: string;
  financial_year_start: string;
  time_zone: string;
  company_email: string;
  company_phone: string;
  currency_name: string;
  currency_symbol: string;
  country_code: string;
  industry_type: string;
  registered_address: string;
  company_user_id: number;
  user_id: number;
  logo: string;
  employee_id: number;
  employee_status: number;
  company_role_id: number;
  role: string;
  company_branch_id: number;
  company_role: CompanyRole;
  employee: Employee;
  reporting_manager: {
    id: number;
    employee_name: string;
    employee_mobile: string;
    user_id: number;
    company_role_id: number;
    designation: string;
    employee_image: string;
  };
  access_right: number;
  documents: {
    incorporation_certificate: string;
    incorporation_date: string;
    incorporation_number: string;
    pan_card: string;
    pan_card_no: string;
    gst_number: string;
    gst_certificate: string;
    trade_license_number: string;
    trade_license: string;
    others: unknown[];
  };
  latitude: string;
  longitude: string;
  total_employee_count: number;
  company_branch: CompanyBranch[];
}

export type GetCompanyRequest = Record<string, never>;

export interface GetCompanyResponse {
  success: boolean;
  status: string;
  code: number;
  message: string;
  data: DetailedCompany[];
}

/**
 * A wifi network registered against a branch, as get-company-branch returns it
 * (the `company_branch.wifis` jsonb column, verbatim). Only the BSSID is
 * consumed on web -- it is what the timeline matches a ping's `wifi_id`
 * against to colour the wifi name green/red (iOS loadVerifiedZoneBssids_S133
 * reads the same `wifis[].bssid` list out of the my-company blob) -- but the
 * SSID travels too so a match can be eyeballed in devtools.
 */
export interface CompanyBranchWifi {
  id: number;
  ssid: string;
  bssid: string;
}

// Branch Types
export interface CompanyBranch {
  id: number;
  company_id: number;
  address: string;
  site_location: string | null;
  site_latitude: string | null;
  site_longitude: string | null;
  latitude: string;
  longitude: string;
  name: string;
  /**
   * The branch's registered wifi networks, when any are saved. Optional
   * because older rows carry null and the field was untyped until the
   * timeline's wifi verified-colouring needed it.
   */
  wifis?: CompanyBranchWifi[] | null;
  branch_type: string;
  gst_number: string;
  gst_certificate: string;
  trade_license_number: string;
  trade_license: string;
  documents: {
    gst_number: string;
    gst_certificate: string;
    trade_license_number: string;
    trade_license: string;
    others: Array<{
      id: string;
      name: string;
      number: string;
      url: string;
    }>;
    [key: string]: unknown; // Allow numeric keys for dynamic documents
  };
  working_day: string;
  saturday_working_days: string | null;
  min_work_days: number;
  working_hours: string;
  flexible_working_hours: number;
  start_time: string;
  end_time: string;
  start_time_utc: string;
  end_time_utc: string;
  break_time: number;
  probation_period: number;
  check_in_grace_period: number;
  check_out_grace_period: number;
  late_period: number;
  half_period: number;
  full_day_absent: number;
  allowed_late_day: number;
  is_shift_rotational: number;
  date_of_joining_validate_month: number;
  salary_requested_upto: number;
  max_duration_for_payment_request: number;
  emi_less_then_total_salary: number;
  employee_of_month_amount: number;
  no_of_employee_of_month: number;
  over_time: number;
  shift: Array<{
    name: string;
    end_time: string;
    start_time: string;
    id: string;
  }>;
  shift_rotational_date: string | null;
  status: number;
  ssid: string | null;
  router_name: string | null;
  geofencing: string | null;
  floor: string | null;
  floor_variation: string | null;
  created_at: string;
  updated_at: string;
  salary_calculation_type: string;
  employee_count: number;
  edit_access: number;
  view_access: number;
  is_home_branch: number;
  today_branch_status: string;
  /**
   * Sandwich-leave policy. Lives on `company_branch`, NOT on the nested
   * `leave` object — `company_leave` has no such column (verified against
   * Aurora). Reading it off `leave` silently yields undefined, which is how
   * the Leave Rules sheet came to report OFF for branches that have it on.
   */
  is_sandwich_leave?: string | null;
  leave?: {
    id: number;
    company_id: number;
    company_branch_id: number;
    leave_type: string;
    leave_days: number;
    leave_date: string;
    leave_status: string;
    is_current: number;
    created_at: string;
    updated_at: string;
    // Leave rule properties
    sick_leave?: number;
    sick_leave_max_month?: number;
    casual_leave?: number;
    casual_leave_max_month?: number;
    max_consequently_casual_leave?: number;
    earned_leave?: number;
    earned_leave_max_month?: number;
    other_leave?: number;
    other_leave_max_month?: number;
    paid_leave_year?: number;
    carry_forward_leave?: number;
    is_carry_forward_leave_allowed?: number;
    holiday?: number;
    is_sandwich_leave?: string;
  };
}

export interface GetCompanyBranchRequest {
  company_id: number;
  include_inactive?: boolean; // Optional parameter to include inactive branches
}

export interface GetCompanyBranchResponse {
  success: boolean;
  code: number;
  message: string;
  data: CompanyBranch[];
}

export interface AddCompanyBranchRequest {
  company_id: number;
  branch_type?: string;
  id?: number; // For update
  // Basic info fields
  name?: string;
  address?: string;
  latitude?: string;
  longitude?: string;
  site_location?: string;
  site_latitude?: string;
  site_longitude?: string;
  // Timing fields
  start_time?: string;
  end_time?: string;
  break_time?: number;
  is_shift_rotational?: number;
  shift_detail?: string;
  working_day?: string;
  saturday_working_days?: string;
  min_work_days?: number;
  flexible_working_hours?: number;
  // Attendance Rules fields
  check_in_grace_period?: number;
  late_period?: number;
  allowed_late_day?: number;
  full_day_absent?: number;
  half_period?: number;
  check_out_grace_period?: number;
  over_time?: number;
  probation_period?: number;
  // Documents fields
  documents?: Array<{
    id: string;
    name: string;
    number: string;
    fullPath?: string;
    sortPath?: string;
    url?: string;
    isVerified?: boolean;
  }>;
  gst_number?: string;
  gst_certificate?: string;
  trade_license_number?: string;
  trade_license?: string;
  // The PAN and incorporation values are deliberately absent: they belong to the
  // company record, not to a branch, and go through saveCompanyDocuments.
  // Geofencing fields (stored on the branch; all strings server-side)
  geofencing?: string;
  ssid?: string;
  router_name?: string;
  floor?: string;
  floor_variation?: string;
}

export interface AddCompanyBranchResponse {
  success: boolean;
  code: number;
  message: string;
  data?: {
    id?: number;
    [key: string]: unknown;
  };
}

export interface AddCompanyLeaveRequest {
  company_id: number;
  branch_id: number;
  id?: string; // For update
  // Leave fields
  sick_leave?: number;
  sick_leave_max_month?: number;
  casual_leave?: number;
  casual_leave_max_month?: number;
  max_consequently_casual_leave?: number;
  earned_leave?: number;
  earned_leave_max_month?: number;
  other_leave?: number;
  other_leave_max_month?: number;
  paid_leave_year?: number;
  carry_forward_leave?: number;
  is_carry_forward_leave_allowed?: number;
  holiday?: number;
  is_sandwich_leave?: string;
}

export interface AddCompanyLeaveResponse {
  success: boolean;
  code: number;
  message: string;
  data?: unknown;
}

export interface GetCompanyRoleRequest {
  company_id: number;
  branch_id?: number | null;
}

export interface GetCompanyRoleResponse {
  code: number;
  message: string;
  data: Array<{
    id: number;
    name: string;
    role: string | null;
    description: string;
    created_at: string;
    updated_at: string;
  }>;
}

// Mutation functions for TanStack Query

// Get company details
/**
 * The companies this person belongs to.
 *
 * "You belong to none" is a real answer, not a failure -- but the server says it
 * with body code 202 and no `data`, and the request helper treats any code other
 * than 200 as an error and throws. That turned the ordinary state of a brand-new
 * account into a failed request, so everything reading this list saw an error
 * rather than an empty list: the sidebar could not tell "no companies" from
 * "could not ask", so it held the company switcher in a permanent loading
 * spinner and hid the very Create Company button that account needs.
 *
 * Answered here as the empty list it is. A genuine failure -- no network, a 500,
 * a rejected token -- still throws, so callers keep being able to tell the two
 * apart.
 */
export const getCompany = async (): Promise<GetCompanyResponse> => {
  const payload = {};
  try {
    return await apiPost<GetCompanyResponse>('get-company', payload);
  } catch (error: unknown) {
    const code = (error as { code?: number })?.code;
    if (code === 202) {
      return {
        success: true,
        status: 'success',
        code: 200,
        message: 'No companies',
        data: [],
      };
    }
    throw error;
  }
};

// Add Company Types
export interface AddCompanyRequest {
  name: string;
  company_email: string;
  company_phone: string;
  financial_year_start: string;
  registered_address: string;
  currency_name: string;
  currency_symbol: string;
  latitude: string;
  longitude: string;
  industry_type: string;
  time_zone: string;
  country_code: string;
  logo?: string;
}

export interface AddCompanyResponse {
  success: boolean;
  code: number;
  message: string;
  data?: unknown;
}

// Update Company Types (uses add-company with id for update)
export interface AddOrUpdateCompanyRequest extends AddCompanyRequest {
  id?: number; // company_id for update (optional, present only for updates)
}

export interface AddOrUpdateCompanyResponse {
  success: boolean;
  code: number;
  message: string;
  data?: {
    id: number;
    name: string;
    company_branch?: Array<{
      id: number;
      company_id: number;
      name: string;
      branch_type: string;
      address?: string;
      latitude?: string;
      longitude?: string;
    }>;
  };
}

// Add or update a company (uses add-company endpoint, with id for updates)
export const addOrUpdateCompany = async (
  request: AddOrUpdateCompanyRequest
): Promise<AddOrUpdateCompanyResponse> => {
  const payload = {
    ...(request.id && { id: request.id }), // Include id only for updates
    name: request.name,
    company_email: request.company_email,
    company_phone: request.company_phone,
    financial_year_start: request.financial_year_start,
    registered_address: request.registered_address,
    currency_name: request.currency_name,
    currency_symbol: request.currency_symbol,
    latitude: request.latitude,
    longitude: request.longitude,
    industry_type: request.industry_type,
    time_zone: request.time_zone,
    country_code: request.country_code,
    ...(request.logo && { logo: request.logo }),
  };
  return apiPost<AddOrUpdateCompanyResponse>('add-company', payload);
};

// Update the company's logo and identity documents. These values are stored on
// the COMPANY record, not on a branch: get-company returns them under
// `documents` and the phone apps read the same company-level keys. Sending them
// to add-company-branch instead looks like it works -- the request succeeds --
// but the branch endpoint copies only the fields on its own list and drops
// these without a word, so the apps never display them.
//
// add-company partial-merges on update (the backend copies only the fields
// present in the request), but its validator requires `name`, so the current
// name is echoed back alongside whatever is being changed.
export interface UpdateCompanyDocumentsRequest {
  id: number;
  name: string;
  logo?: string; // sortPath (short storage path), not the full URL
  pan_card?: string; // sortPath
  pan_card_no?: string;
  incorporation_certificate?: string; // sortPath
  incorporation_number?: string;
  incorporation_date?: string; // YYYY-MM-DD
}

export const updateCompanyDocuments = async (
  request: UpdateCompanyDocumentsRequest,
  // Values this API would otherwise destroy if they were absent -- see
  // saveCompanyDocuments below, which works them out. Spread first so nothing
  // here can override a field the caller is actually changing.
  preserve: Record<string, unknown> = {}
): Promise<AddOrUpdateCompanyResponse> => {
  return apiPost<AddOrUpdateCompanyResponse>('add-company', {
    ...preserve,
    id: request.id,
    name: request.name,
    ...(request.logo && { logo: request.logo }),
    ...(request.pan_card && { pan_card: request.pan_card }),
    ...(request.pan_card_no && { pan_card_no: request.pan_card_no }),
    ...(request.incorporation_certificate && {
      incorporation_certificate: request.incorporation_certificate,
    }),
    ...(request.incorporation_number && { incorporation_number: request.incorporation_number }),
    ...(request.incorporation_date && { incorporation_date: request.incorporation_date }),
  });
};

/**
 * Save some of a company's document fields, leaving the rest as they are.
 *
 * The company is read first for two reasons. One, add-company's validator
 * rejects a request with no `name`, so the current one has to be sent back with
 * whatever is being changed.
 *
 * Two -- and this is specific to THIS backend -- `CompanyRepository::addCompany`
 * rewrites `incorporation_date` on every save, unconditionally
 * (CompanyRepository.php:310-312), so any request that omits it blanks a date
 * that was already stored. The upstream version of this function dropped its
 * read-back because that server was fixed to only write the field when sent;
 * ours has not been, so the stored date is deliberately sent back here. Remove
 * this only alongside a `$data->has('incorporation_date')` guard on the server.
 *
 * Nothing else may be echoed back, and the file paths especially must not be.
 * get-company returns `logo`, `pan_card` and `incorporation_certificate` as
 * display URLs, not the storage keys these columns hold -- it wraps them in
 * Media::url() before answering. Sending those back would rewrite all three
 * columns into absolute URLs pinned to today's media host. It looks fine,
 * because the media helper passes an absolute URL through untouched, and breaks
 * the day the host or the URL scheme changes.
 */
export const saveCompanyDocuments = async (
  companyId: number,
  changes: Omit<UpdateCompanyDocumentsRequest, 'id' | 'name'>
): Promise<AddOrUpdateCompanyResponse> => {
  const res = await getCompany();
  const company = res.data?.find((c) => c.id === companyId);
  if (!company) {
    throw new Error('Company not found');
  }

  // Everything this endpoint would otherwise destroy, read back and resent.
  //
  // add-company does not merge on this backend: several columns are written on
  // EVERY call regardless of whether the request mentioned them, so a request
  // about documents silently rewrites things it has nothing to do with. Each
  // entry below is one of those columns.
  // Resolved by type, not by position. Nothing guarantees the head office is
  // first in the list, and reading a sub-branch here would re-send ITS leave
  // policy and licence number into a call that writes them to the head office --
  // corrupting the very values this block exists to protect. The other
  // head-office lookups added alongside this one match on branch_type too.
  const branches = company.company_branch ?? [];
  const headOffice =
    branches.find(
      (b) => b.branch_type === 'Head Office' || b.branch_type === 'head',
    ) ?? branches[0];
  const leave = headOffice?.leave;

  // Without a leave record there is nothing to re-send, and sending the request
  // anyway would let the unconditional rewrite zero the policy — precisely the
  // outcome this function exists to prevent. Refusing is the safe answer: the
  // document is not saved, and the caller reports why.
  if (headOffice && !leave) {
    throw new Error(
      'Could not read the branch leave policy, so this save was stopped to avoid clearing it. Please try again.',
    );
  }

  const preserve: Record<string, unknown> = {
    // Rewritten unconditionally (CompanyRepository.php:310-312), so omitting it
    // blanks a stored date. Only when the caller is not setting it themselves.
    ...(changes.incorporation_date === undefined && company.documents?.incorporation_date
      ? { incorporation_date: company.documents.incorporation_date }
      : {}),
    // Assigned outside the guarded branch-field loop (CompanyRepository.php:526),
    // so any save that omits it empties a trade licence number nobody edited.
    ...(headOffice?.trade_license_number
      ? { trade_license_number: headOffice.trade_license_number }
      : {}),
    // The big one. addCompany calls CompanyLeaveRepository::saveCompanyLeave on
    // every save, with no guard for "is this request even about leave"
    // (CompanyRepository.php:563-571). That method retires the branch's current
    // policy and writes a fresh one in which every allowance it was not given
    // falls back to zero -- so storing a document would wipe the head office's
    // entire leave entitlement, with nothing on screen to say so. Resending the
    // current values makes the rewrite a no-op.
    ...(leave
      ? {
          sick_leave: leave.sick_leave ?? 0,
          sick_leave_max_month: leave.sick_leave_max_month ?? 0,
          casual_leave: leave.casual_leave ?? 0,
          casual_leave_max_month: leave.casual_leave_max_month ?? 0,
          max_consequently_casual_leave: leave.max_consequently_casual_leave ?? 0,
          earned_leave: leave.earned_leave ?? 0,
          earned_leave_max_month: leave.earned_leave_max_month ?? 0,
          other_leave: leave.other_leave ?? 0,
          other_leave_max_month: leave.other_leave_max_month ?? 0,
          paid_leave_year: leave.paid_leave_year ?? 0,
          carry_forward_leave: leave.carry_forward_leave ?? 0,
          is_carry_forward_leave_allowed: leave.is_carry_forward_leave_allowed ?? 0,
          holiday: leave.holiday ?? 0,
          // Every other writer of this policy sends it, so leaving it out here
          // let the rewrite flip sandwich leave back to its default.
          is_sandwich_leave: leave.is_sandwich_leave ?? 'N',
        }
      : {}),
  };

  // NOT defensible from here: tag_id, location_accuracy, epf, esi and
  // medical_insurance are also written on every save
  // (CompanyRepository.php:282, 327-331) but get-company does not return them,
  // so there is no value to send back. Every add-company call from this app
  // already resets them -- including the company edit form, long before this
  // function existed. Closing that needs the server to guard those writes with
  // has(); it cannot be done from the client.
  return updateCompanyDocuments(
    {
      ...changes,
      id: companyId,
      name: company.name,
    },
    preserve,
  );
};

// Add or update company branch (for timing and attendance rules)
export const addOrUpdateCompanyBranch = async (
  request: AddCompanyBranchRequest
): Promise<AddCompanyBranchResponse> => {
  const payload = {
    company_id: request.company_id,
    ...(request.branch_type && { branch_type: request.branch_type }),
    ...(request.id && { id: request.id }), // For update
    // Basic info fields
    ...(request.name && { name: request.name }),
    ...(request.address && { address: request.address }),
    ...(request.latitude && { latitude: request.latitude }),
    ...(request.longitude && { longitude: request.longitude }),
    ...(request.site_location && { site_location: request.site_location }),
    ...(request.site_latitude && { site_latitude: request.site_latitude }),
    ...(request.site_longitude && { site_longitude: request.site_longitude }),
    // Timing fields
    ...(request.start_time && { start_time: request.start_time }),
    ...(request.end_time && { end_time: request.end_time }),
    ...(request.break_time !== undefined && { break_time: request.break_time }),
    ...(request.is_shift_rotational !== undefined && { is_shift_rotational: request.is_shift_rotational }),
    ...(request.shift_detail && { shift_detail: request.shift_detail }),
    ...(request.working_day && { working_day: request.working_day }),
    ...(request.saturday_working_days && { saturday_working_days: request.saturday_working_days }),
    ...(request.min_work_days !== undefined && { min_work_days: request.min_work_days }),
    ...(request.flexible_working_hours !== undefined && { flexible_working_hours: request.flexible_working_hours }),
    // Attendance Rules fields
    ...(request.check_in_grace_period !== undefined && { check_in_grace_period: request.check_in_grace_period }),
    ...(request.late_period !== undefined && { late_period: request.late_period }),
    ...(request.allowed_late_day !== undefined && { allowed_late_day: request.allowed_late_day }),
    ...(request.full_day_absent !== undefined && { full_day_absent: request.full_day_absent }),
    ...(request.half_period !== undefined && { half_period: request.half_period }),
    ...(request.check_out_grace_period !== undefined && { check_out_grace_period: request.check_out_grace_period }),
    ...(request.over_time !== undefined && { over_time: request.over_time }),
    ...(request.probation_period !== undefined && { probation_period: request.probation_period }),
    // Documents fields
    ...(request.documents && { documents: request.documents }),
    ...(request.gst_number && { gst_number: request.gst_number }),
    ...(request.gst_certificate && { gst_certificate: request.gst_certificate }),
    ...(request.trade_license_number && { trade_license_number: request.trade_license_number }),
    ...(request.trade_license && { trade_license: request.trade_license }),
    // Geofencing fields. Sent whenever present INCLUDING empty, unlike the
    // fields above -- an empty radius is how the field's "leave blank for the
    // default" hint is expressed, so it has to reach the wire rather than being
    // dropped as falsy.
    ...(request.geofencing !== undefined && { geofencing: request.geofencing }),
    ...(request.ssid !== undefined && { ssid: request.ssid }),
    ...(request.router_name !== undefined && { router_name: request.router_name }),
    ...(request.floor !== undefined && { floor: request.floor }),
    ...(request.floor_variation !== undefined && { floor_variation: request.floor_variation }),
  };

  return apiPost<AddCompanyBranchResponse>('add-company-branch', payload);
};

// ── Branch Wi-Fi ─────────────────────────────────────────────────────────────
// A branch's registered Wi-Fi networks, managed via the dedicated branches/wifi
// endpoints (keyed by branch_id, so only usable once the branch exists). Matches
// iOS's "Branch wifi's" screen. All fields are strings except id.

export interface BranchWifi {
  id: number;
  ssid: string;
  bssid: string;
  floor: string;
  floor_variation: string;
  wifi_location: string; // "BRANCH" | "SITE"
}

export interface GetBranchWifisResponse {
  success: boolean;
  code: number;
  message: string;
  data: {
    branch_id: number;
    wifis: BranchWifi[];
    total: number;
  };
}

export interface SaveBranchWifiResponse {
  success: boolean;
  code: number;
  message: string;
  data: {
    branch_id: number;
    wifis: BranchWifi[];
    summary?: { added?: number; updated?: number; total?: number };
  };
}

export interface DeleteBranchWifiResponse {
  success: boolean;
  code: number;
  message: string;
  data: {
    branch_id: number;
    wifis: BranchWifi[];
    summary?: { removed?: number; not_found?: number; remaining?: number };
  };
}

// List a branch's Wi-Fi networks. company_id is mandatory (ensure.company.member).
export const getBranchWifis = async (
  companyId: number,
  branchId: number
): Promise<GetBranchWifisResponse> => {
  return apiPost<GetBranchWifisResponse>('branches/get-wifi', {
    company_id: companyId,
    branch_id: branchId,
  });
};

// Upsert Wi-Fi networks (server upserts each by id). Send one or more.
export const saveBranchWifi = async (
  companyId: number,
  branchId: number,
  wifis: BranchWifi[]
): Promise<SaveBranchWifiResponse> => {
  return apiPost<SaveBranchWifiResponse>('branches/wifi', {
    company_id: companyId,
    branch_id: branchId,
    wifis,
  });
};

// Delete Wi-Fi networks by id. The ids travel in the DELETE body, which is what
// this endpoint reads them from.
export const deleteBranchWifi = async (
  companyId: number,
  branchId: number,
  ids: number[]
): Promise<DeleteBranchWifiResponse> => {
  return apiDelete<DeleteBranchWifiResponse>('branches/wifi', {
    company_id: companyId,
    branch_id: branchId,
    ids,
  });
};

// Add or update company leave rules
export const addOrUpdateCompanyLeave = async (
  request: AddCompanyLeaveRequest
): Promise<AddCompanyLeaveResponse> => {
  const payload = {
    company_id: request.company_id,
    branch_id: request.branch_id,
    ...(request.id && { id: request.id }), // For update
    // Leave fields - convert undefined to 0 for numbers
    sick_leave: request.sick_leave ?? 0,
    sick_leave_max_month: request.sick_leave_max_month ?? 0,
    casual_leave: request.casual_leave ?? 0,
    casual_leave_max_month: request.casual_leave_max_month ?? 0,
    max_consequently_casual_leave: request.max_consequently_casual_leave ?? 0,
    earned_leave: request.earned_leave ?? 0,
    earned_leave_max_month: request.earned_leave_max_month ?? 0,
    other_leave: request.other_leave ?? 0,
    other_leave_max_month: request.other_leave_max_month ?? 0,
    paid_leave_year: request.paid_leave_year ?? 0,
    carry_forward_leave: request.carry_forward_leave ?? 0,
    is_carry_forward_leave_allowed: request.is_carry_forward_leave_allowed ?? 0,
    holiday: request.holiday ?? 0,
    is_sandwich_leave: request.is_sandwich_leave ?? 'N',
  };
  
  return apiPost<AddCompanyLeaveResponse>('add-company-leave', payload);
};

// Company Detail Types (for get-my-company-detail)
export interface CompanyOverview {
  rating: {
    avg_rating: number;
    no_of_rating: number;
  };
  approval: number;
  bill: number;
  company_id: number;
}

export interface CompanyDetailItem {
  id: number;
  incorporation_number: string;
  incorporation_date: string | null;
  pan_card_no: string;
  name: string;
  financial_year_start: string;
  time_zone: string;
  company_email: string;
  company_phone: string;
  currency_name: string;
  currency_symbol: string;
  country_code: string;
  industry_type: string;
  registered_address: string;
  company_user_id: number;
  user_id: number;
  logo: string;
  incorporation_certificate: string;
  pan_card: string;
  employee: {
    id: number;
    unique_id: string;
    company_id: number;
    user_id: number;
    company_branch_id: number;
    employee_name: string;
    employee_mobile: string;
    employee_email: string;
    designation: string;
    [key: string]: unknown;
  };
  role: string;
  access_right: number;
  employee_count: number;
  company_branch: CompanyBranch[];
  [key: string]: unknown;
}

/**
 * One row of `today_attendance` — the logged-in user's attendance state for
 * today, one row per company. Keys mirror the native model
 * (`CompanyTodayAttendance.swift:37-70`); only what the web reads is typed.
 */
export interface MyCompanyTodayAttendance {
  company_id?: number;
  check_in_time?: number;
  check_out_time?: number;
  day_check_in_time?: number;
  /** The server's PAYABLE total for the day, summed from
   *  `company_attendance_master.total_minutes` (`CompanyEmployee.php:1152`).
   *  Not a wall-clock delta — it excludes grace, which is why a 10:53→20:08
   *  day reports 9h, not 9h 15m. */
  total_working_seconds?: number;
  is_auto_check_out?: number;
  is_doing_overtime?: number;
  is_overtime_auto_check_out?: number;
  /**
   * The PER-DAY flexible flag, copied from `company_attendance_master`.
   *
   * NOT interchangeable with the employee's profile flag: the backend fills
   * this only inside its `status == 'PRESENT'` guard
   * (`CompanyEmployee.php:812`), so on a week off, a holiday, or any morning
   * before the first punch it reads 0 — reporting a flexible employee as
   * fixed. Prefer `Employee.is_flexible_time`; keep this as the fallback.
   */
  is_flexible_time?: number | string;
  /** One row PER SESSION for the day — `company_attendance` is not one row per
   *  day. Always sent (`CompanyEmployee.php:827-833`); the per-session
   *  auto-close flags only appear when the backend's `s150_addendum_enabled`
   *  is on, which ships OFF. */
  attendance_log?: Array<{
    check_in_time?: number;
    check_out_time?: number;
    status?: string | null;
    is_auto_check_out?: number;
    is_overtime_auto_check_out?: number;
    total_over_time_minute?: number | string | null;
  }>;
  company_over_time_approval_id?: number | string;
  company_over_time_status?: string | null;
  overtime_approval?: { status?: string | null } | null;
  [key: string]: unknown;
}

export interface GetMyCompanyDetailResponse {
  code: number;
  message: string;
  data: {
    overviews: CompanyOverview[];
    companies: CompanyDetailItem[];
    today_attendance: MyCompanyTodayAttendance[];
    upcoming_holidays: unknown[];
    week_off: unknown[];
    leaves: unknown[];
  };
}

export interface GetMyCompanyDetailRequest {
  user_id: number;
  /** Company timezone when known, else the device's — native sends one always. */
  timezone: string;
  user_device_id?: string;
  today_date?: string;
  start_date?: string;
  end_date?: string;
}

/**
 * Get my company detail — companies, documents and `today_attendance`.
 *
 * The native apps ALWAYS send all six keys, with the three dates as EMPTY
 * STRINGS rather than omitted (`CompanyDashboardViewModel.getMyCompanyDetail`,
 * called with no args from `CompanyTabbarViewController+API:441`). This request
 * is the only source of `today_attendance` — the Chats tab just reads the
 * cached blob it writes.
 *
 * Callers that only need companies/documents can keep calling it with no
 * argument, which sends the historical empty payload unchanged.
 */
export const getMyCompanyDetail = async (
  request?: GetMyCompanyDetailRequest,
): Promise<GetMyCompanyDetailResponse> => {
  const payload: Record<string, unknown> = request
    ? {
        user_id: request.user_id,
        timezone: request.timezone,
        user_device_id: request.user_device_id ?? '',
        today_date: request.today_date ?? '',
        start_date: request.start_date ?? '',
        end_date: request.end_date ?? '',
      }
    : {};
  return apiPost<GetMyCompanyDetailResponse>('get-my-company-detail', payload);
};

// Get company branches
export const getCompanyBranch = async (request: GetCompanyBranchRequest): Promise<GetCompanyBranchResponse> => {
  const payload: Record<string, unknown> = {
    company_id: request.company_id,
  };
  // Include inactive branches if requested
  // Try both snake_case and camelCase formats as different APIs may expect different formats
  if (request.include_inactive !== undefined && request.include_inactive) {
    payload.include_inactive = 1; // Try snake_case first
    // If snake_case doesn't work, uncomment the line below to try camelCase:
    // payload.includeInactive = 1;
  }
  
  const response = await apiPost<GetCompanyBranchResponse>('get-company-branch', payload);
  
  return response;
};

// Update Branch Status Types
export interface UpdateBranchStatusRequest {
  id: number;
  company_id: number;
  status: 'active' | 'inactive';
}

export interface UpdateBranchStatusResponse {
  code: number;
  message: string;
  data?: unknown;
}

/**
 * Update branch status (active or inactive)
 */
export const updateBranchStatus = async (
  request: UpdateBranchStatusRequest
): Promise<UpdateBranchStatusResponse> => {
  const payload = {
    id: request.id,
    company_id: request.company_id,
    status: request.status,
  };
  return apiPost<UpdateBranchStatusResponse>('update-branch-status', payload);
};

// Get company roles (authority levels)
export const getCompanyRoles = async (request: GetCompanyRoleRequest): Promise<GetCompanyRoleResponse> => {
  const payload = {
    company_id: request.company_id,
    ...(request.branch_id !== undefined ? { branch_id: request.branch_id } : {}),
  };
  return apiPost<GetCompanyRoleResponse>('get-company-role', payload);
};

// Performance Review Types
export interface PendingReviewEmployee {
  id: number;
  company_id: number;
  company_branch_id: number;
  employee_name: string;
  employee_mobile: string;
  employee_country_code: string;
  user_id: number;
  company_role_id: number;
  designation: string;
  is_reviewed: 0 | 1;
  average_performance_rating: number;
  reporting_manager_id: number;
  date_of_joining: string;
  reviewed_date: string;
  verification_stage: number;
  employee_image: string;
  isEmailVerified: string;
  employee_of_month: number;
  review_employee: {
    id: number;
    company_id: number;
    employee_name: string;
    employee_mobile: string;
    employee_country_code: string;
    user_id: number;
    company_role_id: number;
    designation: string;
    is_reviewed: 0 | 1;
    average_performance_rating: number;
    reporting_manager_id: number;
    date_of_joining: string;
    employee_image: string;
  } | null;
  avg_count: {
    taskPerformanceScore: number;
    meetingPerformanceScore: number;
    responseTimeScore: number;
    salesLeadScore: number;
    attendanceScore: number;
    managementScore: number;
    overallScore: number;
  };
  reporting_manager: {
    id: number;
    employee_name: string;
    designation: string;
    employee_email: string;
  };
  branch: {
    id: number;
    name: string;
    address: string;
    check_out_grace_period: number;
    check_in_grace_period: number;
    late_period: number;
    half_period: number;
  };
  employee_answer: {
    id: number;
    company_employee_id: number;
    created_at: string;
    year: number;
  } | null;
}

export interface GetPendingReviewEmployeeListRequest {
  company_id: number;
  branch_id: number;
  employee_id: number;
}

export interface GetPendingReviewEmployeeListResponse {
  code: number;
  status: boolean;
  message: string;
  data: PendingReviewEmployee[];
}

export interface ReviewQuestion {
  id: string;
  title: string;
  description: string;
  type: 'number';
  answer?: string;
  created_at: string;
  updated_at: string;
}

export interface GetReviewQuestionsRequest {
  company_id: number;
  company_employee_id: number;
}

export interface GetReviewQuestionsResponse {
  success: boolean;
  code: number;
  message: string;
  data: ReviewQuestion[];
}

export interface ReviewAnswerItem {
  id: string;
  title: string;
  type: string;
  answer: string;
  created_at: string;
  updated_at: string;
  description: string;
}

export interface AddQuestionAnswerRequest {
  company_id: number;
  company_employee_id: number;
  answers: ReviewAnswerItem[];
}

export interface AddQuestionAnswerResponse {
  code: number;
  message: string;
  status?: boolean;
}

// Get pending review employee list
export const getPendingReviewEmployeeList = async (
  request: GetPendingReviewEmployeeListRequest
): Promise<GetPendingReviewEmployeeListResponse> => {
  const payload = {
    company_id: request.company_id,
    branch_id: request.branch_id,
    employee_id: request.employee_id,
  };
  return apiPost<GetPendingReviewEmployeeListResponse>('get-pending-review-employee-list', payload);
};

// Get review questions
export const getReviewQuestions = async (
  request: GetReviewQuestionsRequest
): Promise<GetReviewQuestionsResponse> => {
  const payload = {
    company_id: request.company_id,
    company_employee_id: request.company_employee_id,
  };
  return apiPost<GetReviewQuestionsResponse>('get-question', payload);
};

// Add question answer (submit review)
export const addQuestionAnswer = async (
  request: AddQuestionAnswerRequest
): Promise<AddQuestionAnswerResponse> => {
  const payload = {
    company_id: request.company_id,
    company_employee_id: request.company_employee_id,
    answers: request.answers,
  };
  return apiPost<AddQuestionAnswerResponse>('add-question-answer', payload);
};

// Get Common Company between two users
export interface GetCommonCompanyRequest {
  user_id: number;
  to_user_id: number;
}

export interface CommonCompanyItem {
  id: number;
  name: string;
  company_phone: string;
  time_zone: string;
  logo: string;
  reporting_manager: {
    employee_id: number;
    employee_name: string;
    user_id: number;
  };
}

export interface GetCommonCompanyResponse {
  code: number;
  message: string;
  data: CommonCompanyItem[];
}

export const getCommonCompany = async (
  request: GetCommonCompanyRequest
): Promise<GetCommonCompanyResponse> => {
  const payload = {
    user_id: request.user_id,
    to_user_id: request.to_user_id,
  };
  return apiPost<GetCommonCompanyResponse>('get-common-company', payload);
};

// Delete Company Types
export interface DeleteCompanyRequest {
  company_id: number;
  id: number;
}

export interface DeleteCompanyResponse {
  code: number;
  message: string;
  data: {
    company_ids: number[];
    results: Record<string, {
      code: number;
      message: string;
    }>;
  };
}

/**
 * Delete a company
 */
export const deleteCompany = async (
  request: DeleteCompanyRequest
): Promise<DeleteCompanyResponse> => {
  const payload = {
    company_id: request.company_id,
    id: request.id,
  };
  return apiPost<DeleteCompanyResponse>('delete-company', payload);
};

